import t from 'react-translate';
import { css } from '@emotion/react';
import { MentoringProgramGuide } from 'redux/schemas/models/mentoring-program-guides';
import { ReactComponent as GuidanceIcon } from 'styles/icons/guidance.svg';
import { halfSpacing, quarterSpacing } from 'styles/global_defaults/scaffolding';
import NvIcon from 'shared/components/nv-icon';
import ClickableContainer from 'components/clickable-container';
import NvTruncatedTextWithTooltip from 'shared/components/nv-truncated-text-with-tooltip';
import { useContext, useState } from 'react';
import { AngularContext } from 'react-app';
import { gray6, gray7 } from 'styles/global_defaults/colors';
import { novoEdBlue } from 'athena/styles/colors';
import { config } from '@config/pendo.config.json';

const DEFAULT_DISPLAY_LIMIT = 2;
const TITLE_MAX_CHAR_SIZE = 50;

type GuidanceSectionProps = {
  guides: MentoringProgramGuide[];
  className?: string;
};

const styles = css`
  .guide-list {
    gap: ${halfSpacing}px;
  }
  .guide-item {
    gap: ${halfSpacing}px;
    width: fit-content;
    height: 36px;
    border-radius: ${halfSpacing}px;
    background: linear-gradient(
      91.4deg,
      rgba(235, 179, 129, 0.1) 0.19%,
      rgba(153, 206, 212, 0.1) 98.6%
    );
    border: 1px solid transparent;

    &:hover {
      background: ${gray7};
      border: 1px solid ${gray6};

      i {
        color: ${novoEdBlue};
      }
    }
  }
  .toggle-show-more {
    padding: ${quarterSpacing}px;
    border-radius: ${quarterSpacing}px;

    &:hover {
      background: ${gray7};
    }
  }
`;

const GuidanceSection = ({ guides, className }: GuidanceSectionProps) => {
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const [isExpanded, setIsExpanded] = useState(false);
  const translationBase =
    t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GUIDANCE_SECTION;
  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.guidance.section;

  const setGuideIdParam = (guideId: number) => {
    const params = { guideId };
    $state.go($state.current, params, { notify: false });
  };

  return (
    <div css={styles} className={className}>
      <ul className='list-unstyled guide-list d-flex flex-wrap align-items-center'>
        {(isExpanded ? guides : guides.slice(0, DEFAULT_DISPLAY_LIMIT)).map(
          (guide) => (
            <li key={guide.id}>
              <ClickableContainer
                className='d-flex px-2 guide-item align-items-center semi-bold black'
                onClick={(e) => {
                  e.stopPropagation();
                  setGuideIdParam(guide.id);
                }}
                data-qa={dataQATag.item.showGuide}
                data-qa-id={`${dataQATag.item.showGuide}-${guide.id}`}
                aria-label={translationBase.ARIA_LABEL.SHOW_GUIDE(guide.title)}
              >
                <GuidanceIcon width={16} height={16} />
                <NvTruncatedTextWithTooltip
                  text={guide.title}
                  characterCount={TITLE_MAX_CHAR_SIZE}
                  className='truncated-text d-flex align-items-start'
                />
                <NvIcon className='gray-4' icon='expand' size='xss-smallest' />
              </ClickableContainer>
            </li>
          )
        )}
        {guides.length > DEFAULT_DISPLAY_LIMIT && (
          <li key='toggle'>
            <ClickableContainer
              className='text-regular toggle-show-more semi-bold gray-2'
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded((expanded) => !expanded);
              }}
              data-qa={dataQATag.toggleExpand}
              aria-expanded={isExpanded}
              aria-label={
                isExpanded
                  ? translationBase.ARIA_LABEL.SHOW_LESS_GUIDES()
                  : translationBase.ARIA_LABEL.SHOW_MORE_GUIDES(
                      guides.length - DEFAULT_DISPLAY_LIMIT
                    )
              }
            >
              {isExpanded
                ? translationBase.SHOW_LESS()
                : translationBase.SHOW_MORE(
                    guides.length - DEFAULT_DISPLAY_LIMIT
                  )}
            </ClickableContainer>
          </li>
        )}
      </ul>
    </div>
  );
};

export default GuidanceSection;
